import React, { useEffect, useState, useCallback } from "react";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "components/ui/dialog";
import { toast } from "components/ui/toast";
import getPbTemplate from "./pbTemplate";
import { Pencil, X, Trash2, CheckCircle, Copy } from "lucide-react";
import { SelectField } from "components/ui/select-field";

interface EditPbProps {
    widget: any;
    index: number;
    apiKey: string;
    editWidget: (cfg: any, index: number) => void;
    saveWidget: (obj: any, index: number) => void;
    updateWidget: (obj: any, id: string) => void;
    deleteWidget: (id: string) => void;
}

const EditPb: React.FC<EditPbProps> = ({ widget, index, apiKey, editWidget, saveWidget, updateWidget, deleteWidget }) => {
    const [pbTemplate, setPbTemplate] = useState<string>("");
    const [showModal, setShowModal] = useState(false);
    const [embedHtml, setEmbedHtml] = useState({ height: "", width: "" });
    const [startEdit, setStartEdit] = useState(false);
    const [edited, setEdited] = useState(false);

    const buildTemplate = useCallback(
        (cfg: any) => {
            const htmlString = getPbTemplate(cfg, apiKey, widget?.leadsGroup);
            const blob = new Blob([htmlString], { type: "text/html" });
            const url = URL.createObjectURL(blob);
            setPbTemplate(url);
        },
        [apiKey, widget?.leadsGroup]
    );

    const copyPBToClipboard = async () => {
        if (!widget.widget_url) return;
        const embedText = `<embed src="${widget.widget_url}" width="${embedHtml.width}" height="${embedHtml.height}">`;
        try {
            await navigator.clipboard.writeText(embedText);
            setShowModal(false);
            setEmbedHtml({ height: "", width: "" });
            toast.success("Widget copied successfully!");
        } catch (err) {
            console.error("❌ Failed to copy text:", err);
            toast.error("Failed to copy embed code");
        }
    };

    const savePromotionData = useCallback(() => {
        const obj = {
            name: widget.name,
            tag_name: "PromotionBanner",
            html_content: getPbTemplate(widget?.config, apiKey, widget?.leadsGroup),
            json_content: widget?.config,
        };
        if (widget.saved) updateWidget(obj, widget.id);
        else saveWidget(obj, index);
    }, [apiKey, widget, index, updateWidget, saveWidget]);

    const handleWidgetData = () => {
        setEdited(false);
        savePromotionData();
    };

    useEffect(() => {
        buildTemplate(widget.config);
    }, [widget?.config]);

    return (
        <div className="mt-3 w-full custom-new-card mb-4 rounded-2xl capitalize">
            <h6 className="text-center text-lg font-semibold mb-2 w-full">{widget.name}</h6>

            <div className="rounded-2xl relative border border-line dark:border-gray-700 overflow-hidden">
                <iframe title="Promotional banner preview" src={pbTemplate ? pbTemplate : undefined} className="w-full" style={{ height: 226 }} />

                {widget.saved && widget.widget_url && (
                    <>
                        <Button
                            size="icon"
                            variant="ghost"
                            className="absolute top-2 right-2 bg-blue-50 dark:bg-slate-800 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md"
                            onClick={() => setShowModal(true)}
                        >
                            <Copy />
                        </Button>

                        <Dialog open={showModal} onOpenChange={setShowModal}>
                            <DialogContent className="max-w-md">
                                <DialogHeader>
                                    <DialogTitle>Widget Config</DialogTitle>
                                </DialogHeader>

                                <div className="grid grid-cols-2 gap-4 py-3">
                                    <div>
                                        <label className="text-sm font-medium mb-1 block">Height</label>
                                        <Input
                                            type="number"
                                            placeholder="Enter Height"
                                            value={embedHtml.height}
                                            onChange={(e) =>
                                                setEmbedHtml((prev) => ({ ...prev, height: e.target.value }))
                                            }
                                        />
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium mb-1 block">Width</label>
                                        <Input
                                            type="number"
                                            placeholder="Enter Width"
                                            value={embedHtml.width}
                                            onChange={(e) =>
                                                setEmbedHtml((prev) => ({ ...prev, width: e.target.value }))
                                            }
                                        />
                                    </div>
                                </div>

                                <div className="flex justify-end gap-2 pt-4">
                                    <Button variant="secondary" onClick={() => setShowModal(false)}>
                                        Close
                                    </Button>
                                    <Button
                                        disabled={!embedHtml.width || !embedHtml.height}
                                        onClick={copyPBToClipboard}
                                    >
                                        Copy
                                    </Button>
                                </div>
                            </DialogContent>
                        </Dialog>
                    </>
                )}
            </div>

            <div className="w-full flex justify-end mt-3 gap-2">
                <Button
                    variant="primary"
                    className="rounded-full"
                    onClick={() => setStartEdit((prev) => !prev)}
                >
                    {startEdit ? (
                        <>
                            <X className="h-4 w-4" /> Cancel
                        </>
                    ) : (
                        <>
                            <Pencil className="h-4 w-4" /> Edit
                        </>
                    )}
                </Button>

                {widget.saved && (
                    <Button
                        variant="destructive"
                        className="rounded-full"
                        disabled={!widget.saved}
                        onClick={() => deleteWidget(widget.id)}
                    >
                        <Trash2 className="h-4 w-4" /> Delete
                    </Button>
                )}

                {(edited || !widget.saved) && (
                    <Button
                        className="rounded-full"
                        onClick={handleWidgetData}
                    >
                        <CheckCircle className="h-4 w-4" /> Save
                    </Button>
                )}
            </div>

            {startEdit && widget?.config && (
                <div className="mt-3">
                    <div className="text-center text-lg font-semibold mb-3 text-gray-800 dark:text-gray-100">
                        Edit Promotion Banner
                    </div>
                    <form className="space-y-3">
                        {/* === Background === */}
                        <div className="font-semibold underline text-gray-700 dark:text-gray-300">Background</div>
                        <div>
                            <label className="form-label">Background Color</label>
                            <Input
                                type="color"
                                value={widget?.config?.background}
                                onChange={(e) => {
                                    const updatedWidget = {
                                        ...widget.config,
                                        background: e.target.value,
                                    }
                                    editWidget(updatedWidget, index);
                                    setEdited(true)
                                }
                                }
                            />
                        </div>

                        {/* === Direction === */}
                        <div className="font-semibold underline text-gray-700 dark:text-gray-300 mt-2">Direction</div>
                        <SelectField
                            className="w-full border rounded-lg p-2 text-sm"
                            value={widget?.config?.direction || "center"}
                            onChange={(e) => {
                                const updatedWidget = {
                                    ...widget.config,
                                    direction: e.target.value,
                                }
                                editWidget(updatedWidget, index);
                                setEdited(true)
                            }}

                        >
                            <option value="start">Start</option>
                            <option value="center">Center</option>
                            <option value="end">End</option>
                        </SelectField>

                        {/* === Link === */}
                        <div className="font-semibold underline text-gray-700 dark:text-gray-300 mt-2">Link</div>
                        <Input
                            type="text"
                            placeholder="Enter link"
                            value={widget?.config?.link || ""}
                            onChange={(e) => {
                                const updatedWidget = {
                                    ...widget.config,
                                    link: e.target.value,
                                }
                                editWidget(updatedWidget, index);
                                setEdited(true)
                            }
                            }
                        />

                        {/* === Text Groups === */}
                        {["first", "second", "third"].map((key) => (
                            <React.Fragment key={key}>
                                <div className="font-semibold underline text-gray-700 dark:text-gray-300 mt-2 capitalize">
                                    {key} Text Group
                                </div>
                                <div className="grid grid-cols-3 gap-2">
                                    <div>
                                        <label className="form-label">Text</label>
                                        <Input
                                            type="text"
                                            value={widget?.config?.[key]?.text || ""}
                                            onChange={(e) => {
                                                const updatedWidget = {
                                                    ...widget.config,
                                                    [key]: { ...widget.config?.[key], text: e.target.value }
                                                }
                                                editWidget(updatedWidget, index);
                                                setEdited(true)
                                            }
                                            }
                                        />
                                    </div>
                                    <div>
                                        <label className="form-label">Color</label>
                                        <Input
                                            type="color"
                                            value={widget?.config[key]?.color || "#000000"}
                                            onChange={(e) => {
                                                const updatedWidget = {
                                                    ...widget.config,
                                                    [key]: { ...widget.config?.[key], color: e.target.value }
                                                }
                                                editWidget(updatedWidget, index);
                                                setEdited(true)
                                            }
                                            }
                                        />
                                    </div>
                                    <div>
                                        <label className="form-label">Font Size</label>
                                        <Input
                                            type="number"
                                            value={widget?.config[key]?.size || ""}
                                            onChange={(e) => {
                                                const updatedWidget = {
                                                    ...widget.config,
                                                    [key]: { ...widget.config?.[key], size: +e.target.value }
                                                }
                                                editWidget(updatedWidget, index);
                                                setEdited(true)
                                            }
                                            }
                                        />
                                    </div>
                                </div>
                            </React.Fragment>
                        ))}

                        {/* === Input === */}
                        <div className="font-semibold underline text-gray-700 dark:text-gray-300 mt-2">Input</div>
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <label className="form-label">Text</label>
                                <Input
                                    type="text"
                                    value={widget?.config?.input?.text || ""}
                                    onChange={(e) => {
                                        const updatedWidget = {
                                            ...widget.config,
                                            input: { ...widget.config?.input, text: e.target.value }
                                        }
                                        editWidget(updatedWidget, index);
                                        setEdited(true)
                                    }
                                    }
                                />
                            </div>
                            <div>
                                <label className="form-label">Width</label>
                                <Input
                                    type="number"
                                    value={widget?.config?.input?.width || ""}
                                    onChange={(e) => {
                                        const updatedWidget = {
                                            ...widget.config,
                                            input: { ...widget.config?.input, width: +e.target.value }
                                        }
                                        editWidget(updatedWidget, index);
                                        setEdited(true)
                                    }
                                    }
                                />
                            </div>
                        </div>

                        {/* === Button === */}
                        <div className="font-semibold underline text-gray-700 dark:text-gray-300 mt-2">Button</div>
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <label className="form-label">Text</label>
                                <Input
                                    type="text"
                                    value={widget?.config?.button?.text || ""}
                                    onChange={(e) => {
                                        const updatedWidget = {
                                            ...widget.config,
                                            button: { ...widget.config?.button, text: e.target.value }
                                        }
                                        editWidget(updatedWidget, index);
                                        setEdited(true)
                                    }
                                    }
                                />
                            </div>
                            <div>
                                <label className="form-label">Color</label>
                                <Input
                                    type="color"
                                    value={widget?.config?.button?.color || "#000000"}
                                    onChange={(e) => {
                                        const updatedWidget = {
                                            ...widget.config,
                                            button: { ...widget.config?.button, color: e.target.value }
                                        }
                                        editWidget(updatedWidget, index);
                                        setEdited(true)
                                    }
                                    }
                                />
                            </div>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
};

export default EditPb;
